"use client";

import React from "react";
import { Martini, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const cocktails = [{
  id: 1,
  name: "Margarita",
  description: "A classic menu favorite - crisp, citrusy, and perfectly balanced tequila cocktail from our collection",
  price: "500 LEK",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/cocktail_margarita.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/cocktail_margarita.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/cocktail_margarita.png-wvc-sizes",
  alt: "Classic Margarita cocktail with a salted rim"
}, {
  id: 2,
  name: "Negroni",
  description: "An iconic menu staple - bold and sophisticated Italian aperitivo served from our premium selection",
  price: "500 LEK",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/cocktail_negroni.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/cocktail_negroni.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/cocktail_negroni.png-wvc-sizes",
  alt: "Sophisticated Negroni cocktail with an orange twist"
}, {
  id: 3,
  name: "Pina Colada",
  description: "A refreshing menu classic - creamy coconut and pineapple blend that brings tropical vibes to every sip",
  price: "500 LEK",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/cocktail_pina_colada.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/cocktail_pina_colada.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/cocktail_pina_colada.png-wvc-sizes",
  alt: "Tropical Pina Colada cocktail with a pineapple garnish"
}, {
  id: 4,
  name: "Espresso Martini",
  description: "The perfect pick-me-up - rich espresso shaken with premium vodka and coffee liqueur for a smooth finish",
  price: "600 LEK",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/cocktail_espresso_martini.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/cocktail_espresso_martini.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/cocktail_espresso_martini.png-wvc-sizes",
  alt: "Elegant Espresso Martini with coffee bean garnish"
}, {
  id: 5,
  name: "Mojito",
  description: "A burst of freshness - muddled mint, lime, and white rum topped with a splash of soda water",
  price: "550 LEK",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/cocktail_mojito.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/cocktail_mojito.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/cocktail_mojito.png-wvc-sizes",
  alt: "Refreshing Mojito with fresh mint and lime"
}, {
  id: 6,
  name: "Aperol Spritz",
  description: "The ultimate sunset drink - bright, bubbly, and bittersweet with prosecco and a splash of soda",
  price: "500 LEK",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/cocktail_aperol_spritz.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/cocktail_aperol_spritz.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678745/img/cocktail_aperol_spritz.png-wvc-sizes",
  alt: "Vibrant Aperol Spritz with an orange slice"
}];
export default function TheCocktailHour() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-cocktail-hour" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      <div className="pointer-events-none absolute top-0 left-1/4 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-lg bg-primary/10 blur-[100px]" />
      <div className="pointer-events-none absolute right-0 bottom-0 h-[500px] w-[500px] translate-x-1/3 translate-y-1/3 rounded-lg bg-primary/5 blur-[120px]" />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`mb-16 flex flex-col items-center text-center transition-all duration-1000 ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <div className="mb-4 inline-flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary backdrop-blur-sm">
            <Sparkles className="size-4" />
            <span>After Dark at Frozen Paradise</span>
          </div>
          <h2 className="font-default mb-6 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
            The Cocktail Hour
          </h2>
          <p className="max-w-2xl text-lg text-muted-foreground">
            As the sun sets over Saranda, our bright cafe transforms into a sleek lounge. 
            Experience our signature drinks, where premium spirits meet our passion for flavor.
          </p>
        </div>

        <div className="mx-auto max-w-6xl">
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-6">
              {cocktails.map((cocktail, i) => <CarouselItem key={cocktail.id} data-index={i} className="pl-4 md:basis-1/2 md:pl-6 lg:basis-1/3">
                  <Card className="group relative h-full overflow-hidden border-primary/20 bg-card/50 backdrop-blur-md transition-colors hover:border-primary/50">
                    <div className="absolute inset-0 bg-gradient-to-b from-primary/0 via-primary/0 to-primary/5 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                    
                    <div className="relative p-6 pb-0">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="h-40 w-40 rounded-lg bg-primary/30 blur-3xl transition-all duration-500 group-hover:bg-primary/40 group-hover:blur-[40px]" />
                      </div>
                      
                      <div className="relative mx-auto aspect-square w-4/5 overflow-hidden drop-shadow-2xl">
                        <img src={cocktail.imageSrc} data-wvc-srcset={cocktail.imageSrcset} data-wvc-sizes={cocktail.imageSizes} alt={cocktail.alt} loading="lazy" className="h-full w-full object-contain transition-transform duration-700 group-hover:scale-110" />
                      </div>
                    </div>

                    <CardHeader className="relative z-10 text-center">
                      <CardTitle className="font-default text-2xl text-foreground">
                        {cocktail.name}
                      </CardTitle>
                      <CardDescription className="text-primary font-medium">
                        {cocktail.price}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="relative z-10 text-center">
                      <p className="text-sm text-muted-foreground">
                        {cocktail.description}
                      </p>
                    </CardContent>

                    <CardFooter className="relative z-10 justify-center pb-8">
                      <Button variant="outline" className="border-primary/30 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground">
                        <Martini className="mr-2 size-4" />
                        Order Now
                      </Button>
                    </CardFooter>
                  </Card>
                </CarouselItem>)}
            </CarouselContent>
            
            <div className="mt-12 flex items-center justify-center gap-4 md:hidden">
              <CarouselPrevious className="static translate-y-0 border-primary/30 bg-card/50 text-primary hover:bg-primary hover:text-primary-foreground" />
              <CarouselNext className="static translate-y-0 border-primary/30 bg-card/50 text-primary hover:bg-primary hover:text-primary-foreground" />
            </div>
            
            <div className="hidden md:block">
              <CarouselPrevious className="-left-16 border-primary/30 bg-card/50 text-primary hover:bg-primary hover:text-primary-foreground" />
              <CarouselNext className="-right-16 border-primary/30 bg-card/50 text-primary hover:bg-primary hover:text-primary-foreground" />
            </div>
          </Carousel>
        </div>

        <div className="mt-16 flex justify-center">
          <Button asChild size="lg" className="group rounded-lg px-8">
            <Link to="/OurMenuPage">
              View Full Drinks Menu
              <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}